import { User } from 'types/user';
import { FC, useEffect, useState, useCallback, useRef, useMemo } from 'react';
import { useLazyGlobalSearchQuery } from 'store/slices/api';
import {
  Box,
  CircularProgress,
  InputAdornment,
  TextField,
  Typography,
  Autocomplete,
} from '@mui/material';
import { GlobalSearchResponse } from 'types';
import { IconSearch } from '@tabler/icons-react';
import { useSelector } from 'store';
import { useNavigate } from 'react-router-dom';
import _ from 'lodash';
import useSnackbar from 'hooks/useSnackbar';
import { handleErr } from 'utils/functions';
import { ALL_RECORD_TYPES_BY_MODEL } from 'constants/recordTypes';
import { ALL_APPS, ALL_VIEWS } from 'constants/appConstants';
import { PylonAction } from 'constants/pylonConstants';

declare global {
  interface Window {
    pylon: {
      chat_settings: {
        app_id: string;
        email: string;
        name: string;
        avatar_url?: string;
        email_hash?: string;
      };
    };
    Pylon: (action: PylonAction, callback?: () => void) => void;
  }
}

const NO_RESULTS_ID = 'no-results';

export type GlobalSearchProps = {
  user: User | null;
};

const GlobalSearch: FC<GlobalSearchProps> = ({ user }) => {
  const [searchInput, setSearchInput] = useState('');
  const [searchResults, setSearchResults] = useState<GlobalSearchResponse[]>(
    []
  );
  const [isDebouncing, setIsDebouncing] = useState(false);
  const [search, { isFetching }] = useLazyGlobalSearchQuery();
  const { drawerOpen } = useSelector((state) => state.menu);
  const navigate = useNavigate();
  const { dispatchErrorSnackbar } = useSnackbar();

  const currentDebouncedSearchRef = useRef<ReturnType<
    typeof _.debounce
  > | null>(null);

  const debouncedSearch = useCallback(
    _.debounce(async (query: string) => {
      if (query.trim()) {
        try {
          const results = await search(query.trim()).unwrap();
          setSearchResults(results);
        } catch (err) {
          handleErr(err, (errMessage: string) => {
            dispatchErrorSnackbar(errMessage);
          });
        }
      } else {
        setSearchResults([]);
      }
      setIsDebouncing(false);
    }, 200),
    [search, dispatchErrorSnackbar]
  );

  useEffect(() => {
    setIsDebouncing(true);
    currentDebouncedSearchRef.current = debouncedSearch;
    debouncedSearch(searchInput);
    return () => {
      if (currentDebouncedSearchRef.current) {
        currentDebouncedSearchRef.current.cancel();
        setIsDebouncing(false);
      }
    };
  }, [searchInput, debouncedSearch]);

  const resultsOptions = useMemo(() => {
    if (
      searchResults.length === 0 &&
      searchInput.trim() &&
      !isFetching &&
      !isDebouncing
    ) {
      return [
        {
          display: `No results found for "${searchInput}"`,
          id: NO_RESULTS_ID,
          model: NO_RESULTS_ID,
        },
      ];
    }
    return searchResults;
  }, [searchResults, searchInput, isFetching, isDebouncing]);

  if (!user || !drawerOpen) {
    return <></>;
  }

  return (
    <Autocomplete
      freeSolo
      options={resultsOptions}
      value={searchInput}
      getOptionLabel={(option) =>
        typeof option === 'string' ? option : option.display
      }
      loading={isFetching || isDebouncing}
      onChange={(_e, value) => {
        const selectedValue = value as GlobalSearchResponse;
        if (!selectedValue?.id || selectedValue.id === NO_RESULTS_ID) return;

        const model = _.toLower(selectedValue.model);
        const recordType = ALL_RECORD_TYPES_BY_MODEL[model];
        if (recordType) {
          if (recordType.view) {
            const view = ALL_VIEWS[recordType.view].path;
            navigate(`${view}/${selectedValue.id}`);
          } else {
            const app = ALL_APPS[recordType.app].path;
            navigate(`${app}/${selectedValue.id}`);
          }
          setSearchInput('');
        }
      }}
      onInputChange={(e, newValue) => {
        setSearchInput(newValue);
      }}
      groupBy={
        searchResults.length > 0
          ? (option) => {
              const model = _.toLower(option.model);
              return ALL_RECORD_TYPES_BY_MODEL[model]?.pluralName || 'Other';
            }
          : undefined
      }
      renderInput={(params) => (
        <TextField
          {...params}
          variant="outlined"
          size="small"
          placeholder="Search Anything"
          InputProps={{
            ...params.InputProps,
            startAdornment: (
              <InputAdornment position="start">
                <IconSearch />
              </InputAdornment>
            ),
            endAdornment: (
              <>
                {isFetching ? (
                  <CircularProgress color="inherit" size={20} />
                ) : null}
                {params.InputProps.endAdornment}
              </>
            ),
          }}
        />
      )}
      renderOption={(props, option) => {
        const isNoResults = option.id === NO_RESULTS_ID;

        return (
          <li
            {...props}
            style={
              isNoResults
                ? { pointerEvents: 'none', padding: '8px 16px' }
                : undefined
            }
          >
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <Typography color={isNoResults ? 'text.secondary' : undefined}>
                {option.display}
              </Typography>
            </Box>
          </li>
        );
      }}
    />
  );
};

export default GlobalSearch;
